import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, computed } from 'vue'
import { useClipboard } from '@vueuse/core'

import EmbedViaIframePopover from './EmbedViaIframePopover.vue'
import LInput from '@/components/core/form/Input.vue'
import LButton from '@/components/core/Button.vue'
import DeleteModal from '@/components/core/DeleteModal.vue'
import {
  useDeleteShareToken,
  useUpdateShareToken,
} from '@/composables/share-token'
import { useContext } from '@/composables/context'
import {
  PrivacySetting,
  privacySettings as allPrivacySettings,
  ShareToken,
} from '@/types/trip'
import { logEvent } from '@/utils/utility-manager'

export interface ShareTokenCardProps {
  tripId: string
  shareToken: ShareToken
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ShareTokenCard',
  props: {
    tripId: null,
    shareToken: null
  },
  setup(__props: any) {

const props = __props as {
  tripId: string
  shareToken: ShareToken
};



const { i18n } = useContext()

const isEditMode = ref(false)

// Generate the share link
const shareLink = computed(() => {
  return `${process.env.LAMBUS_JOURNAL}/${props.shareToken.token}`
})

const name = ref(props.shareToken.name)

const privacySettings = ref<Record<PrivacySetting, boolean>>(
  {} as Record<PrivacySetting, boolean>,
)

watch(
  () => props.shareToken,
  (newToken) => {
    const tokenSettings = newToken.privacySettings || []
    privacySettings.value = allPrivacySettings.reduce(
      (acc, setting) => {
        acc[setting] = tokenSettings.includes(setting)
        return acc
      },
      {} as Record<PrivacySetting, boolean>,
    )

    name.value = newToken.name
  },
  { immediate: true },
)

const description = computed(() => {
  const checkedCount = Object.values(privacySettings.value).filter(
    Boolean,
  ).length
  if (checkedCount === 0) {
    return i18n.t('shareThisTrip.privacySettings.levelDescriptions.none')
  } else if (checkedCount === 1) {
    const checkedSetting = Object.entries(privacySettings.value).find(
      ([, isChecked]) => isChecked,
    )?.[0]
    return i18n.t('shareThisTrip.privacySettings.levelDescriptions.one', {
      privacySetting: i18n.t(
        `shareThisTrip.privacySettings.types.${checkedSetting}`,
      ),
    })
  } else if (checkedCount < allPrivacySettings.length) {
    return i18n.t('shareThisTrip.privacySettings.levelDescriptions.custom')
  } else {
    return i18n.t('shareThisTrip.privacySettings.levelDescriptions.all')
  }
})

const { isLoading: isLoadingSave, mutateAsync: updateShareToken } =
  useUpdateShareToken(props.tripId)
const { isLoading: isLoadingDelete, mutateAsync: deleteShareToken } =
  useDeleteShareToken(props.tripId)

const isLoading = computed(() => isLoadingSave.value || isLoadingDelete.value)

async function handleSave() {
  const checkedPrivacySettings = Object.entries(privacySettings.value)
    .filter(([, isChecked]) => isChecked)
    .map(([setting]) => setting as PrivacySetting)

  logEvent('trip_share_token_updated', {
    privacySettingsCount: checkedPrivacySettings.length,
  })

  await updateShareToken({
    token: props.shareToken.token,
    name: name.value,
    privacySettings: checkedPrivacySettings,
  })

  isEditMode.value = false
}

function handleCancel() {
  logEvent('trip_share_token_edit_cancelled')

  // Reset to original settings
  const tokenSettings = props.shareToken.privacySettings || []
  privacySettings.value = allPrivacySettings.reduce(
    (acc, setting) => {
      acc[setting] = tokenSettings.includes(setting)
      return acc
    },
    {} as Record<PrivacySetting, boolean>,
  )

  name.value = props.shareToken.name

  isEditMode.value = false
}

function enterEditMode() {
  logEvent('trip_share_token_edit_started')
  isEditMode.value = true
}

async function handleDelete(shareToken: ShareToken) {
  logEvent('trip_share_token_deleted')
  await deleteShareToken(shareToken.token)
}

// Clipboard functionality -----------------------------------------------------

const { copied, copy } = useClipboard()

async function copyToClipboard(text: string) {
  logEvent('trip_share_token_link_copied')
  await copy(text)
}

return { __sfc: true,props, i18n, isEditMode, shareLink, name, privacySettings, description, isLoadingSave, updateShareToken, isLoadingDelete, deleteShareToken, isLoading, handleSave, handleCancel, enterEditMode, handleDelete, copied, copy, copyToClipboard, EmbedViaIframePopover, LInput, LButton, DeleteModal, allPrivacySettings, logEvent }
}

})